import React, { useState, useEffect, useMemo } from 'react';

import { Button, Row, Col, Tab, Tabs } from 'react-bootstrap';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';

import SwalDialog from 'lib/utils/toast';
import { ReportType } from 'lib/enums/ReportGeneratrionEnums';
import { FILE_EXPORTS_GENERATE_REPORTS } from 'lib/networking/endpoints';

import AssignBrandsSection from 'components/manage_brands/AssignBrandsSection';
import BrandCleanupActionsSection from 'components/manage_brands/BrandCleanupActionsSection';
import BrandCleanupFilters from 'components/manage_brands/BrandCleanupFilters';
import BrandCleanupProductsTable from 'components/manage_brands/BrandCleanupProductsTable';
import BrandCleanupModal from 'components/manage_brands/BrandCleanupModal';
import Container from 'components/shared/Container';
import {
  CLEANUP_BRANDS_OF_SELECTED_PRODUCTS,
  GET_BRANDS_FOR_BRAND_CLEANUP,
  GET_CATEGORIES_FOR_BRAND_CLEANUP,
  GET_FOR_BRAND_CLEANUP,
  GET_DISTRIBUTOR_BRAND_NAMES_FOR_BRAND_CLEANUP,
  GET_SUGGESTED_BRANDS_FOR_BRAND_CLEANUP,
} from 'lib/networking/endpoints';
import DuplicateBrandsView from 'components/manage_brands/DuplicateBrandsView';
import DuplicateUnverifiedBrandsView from 'components/manage_brands/DuplicateUnverifiedBrandsView';
import {
  L2_CATEGORY_LEVEL,
  L3_CATEGORY_LEVEL,
  L4_CATEGORY_LEVEL,
  TEN_PAGE_SIZE,
} from 'lib/constants';
import useGet from 'lib/hooks/useGet';
import UserPermission from 'lib/enums/UserPermission';
import usePost from 'lib/hooks/usePost';
import useUserPermissions from 'lib/hooks/useUserPermissions';

const BRAND_CLEANUP_TABS = {
  BRAND_CLEANUP: 'BRAND_CLEANUP',
  DUPLICATE_VERIFIED_BRANDS: 'DUPLICATE_VERIFIED_BRANDS',
  DUPLICATE_UNVERIFIED_BRANDS: 'DUPLICATE_UNVERIFIED_BRANDS',
};

const BRAND_CLEANUP_TABS_TITLE = {
  [BRAND_CLEANUP_TABS.BRAND_CLEANUP]: 'Brand Cleanup',
  [BRAND_CLEANUP_TABS.DUPLICATE_VERIFIED_BRANDS]: 'Duplicate Verified Brands',
  [BRAND_CLEANUP_TABS.DUPLICATE_UNVERIFIED_BRANDS]: 'Duplicate Unverified Brands',
};

function BrandCleanupView() {
  const hasUserPermissions = useUserPermissions();

  const hasCleanupBrandsPermission = useMemo(
    () => hasUserPermissions([UserPermission.CLEANUP_BRANDS]),
    [hasUserPermissions],
  );

  const [activeTabKey, setActiveTabKey] = useState(BRAND_CLEANUP_TABS.BRAND_CLEANUP);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(TEN_PAGE_SIZE);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedSuggestedBrand, setSelectedSuggestedBrand] = useState(null);
  const [selectedDistributorBrandName, setSelectedDistributorBrandName] = useState(null);
  const [selectedL2Category, setSelectedL2Category] = useState(null);
  const [selectedL3Category, setSelectedL3Category] = useState(null);
  const [selectedL4Category, setSelectedL4Category] = useState(null);
  const [selectedSuggestedAction, setSelectedSuggestedAction] = useState(null);
  const [showBrandCleanupModal, setShowBrandCleanupModal] = useState(false);
  const [showAssignBrandsSection, setShowAssignBrandsSection] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedSimilarityScore, setSelectedSimilarityScore] = useState(null);
  const [traineeWorkOnly, setTraineeWorkOnly] = useState(false);
  const [GMVProductsOnly, setGMVProductsOnly] = useState(false);
  const [minimumGMV, setMinimumGMV] = useState(null);

  const {
    data: { data: products = [] } = {},
    loading: loadingProducts,
    refetch: refetchProducts,
  } = useGet(GET_FOR_BRAND_CLEANUP, {
    brand_id: selectedBrand?.value || null,
    global_vendor_id: selectedVendor?.value || null,
    suggested_brand_id: selectedSuggestedBrand?.value || null,
    distributor_brand_name: selectedDistributorBrandName?.value || null,
    l2_category_id: selectedL2Category?.value || null,
    l3_category_id: selectedL3Category?.value || null,
    l4_category_id: selectedL4Category?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    confidence_score: selectedSimilarityScore || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });

  const {
    data: { count: productCount = 0 } = {},
    loading: loadingProductCount,
    refetch: refetchProductCount,
  } = useGet(GET_FOR_BRAND_CLEANUP, {
    brand_id: selectedBrand?.value || null,
    global_vendor_id: selectedVendor?.value || null,
    suggested_brand_id: selectedSuggestedBrand?.value || null,
    distributor_brand_name: selectedDistributorBrandName?.value || null,
    l2_category_id: selectedL2Category?.value || null,
    l3_category_id: selectedL3Category?.value || null,
    l4_category_id: selectedL4Category?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    confidence_score: selectedSimilarityScore || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
    count_only: true,
  });

  const {
    data: { data: brands = [] } = {},
    loading: loadingBrands,
    refetch: refetchBrands,
  } = useGet(GET_BRANDS_FOR_BRAND_CLEANUP, {
    global_vendor_id: selectedVendor?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const {
    data: { data: suggestedBrands = [] } = {},
    loading: loadingSuggestedBrands,
    refetch: refetchSuggestedBrands,
  } = useGet(GET_SUGGESTED_BRANDS_FOR_BRAND_CLEANUP, {
    global_vendor_id: selectedVendor?.value || null,
    brand_id: selectedBrand?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const {
    data: { data: distributorBrandNames = [] } = {},
    loading: loadingDistributorBrandNames,
    refetch: refetchDistributorBrandNames,
  } = useGet(GET_DISTRIBUTOR_BRAND_NAMES_FOR_BRAND_CLEANUP, {
    global_vendor_id: selectedVendor?.value || null,
    brand_id: selectedBrand?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const {
    data: { data: l2Categories = [] } = {},
    loading: loadingL2Categories,
    refetch: refetchL2Categories,
  } = useGet(GET_CATEGORIES_FOR_BRAND_CLEANUP, {
    category_level: L2_CATEGORY_LEVEL,
    global_vendor_id: selectedVendor?.value || null,
    brand_id: selectedBrand?.value || null,
    suggested_brand_id: selectedSuggestedBrand?.value || null,
    distributor_brand_name: selectedDistributorBrandName?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const {
    data: { data: l3Categories = [] } = {},
    loading: loadingL3Categories,
    refetch: refetchL3Categories,
  } = useGet(GET_CATEGORIES_FOR_BRAND_CLEANUP, {
    category_level: L3_CATEGORY_LEVEL,
    global_vendor_id: selectedVendor?.value || null,
    brand_id: selectedBrand?.value || null,
    suggested_brand_id: selectedSuggestedBrand?.value || null,
    distributor_brand_name: selectedDistributorBrandName?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    l2_category_id: selectedL2Category?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const {
    data: { data: l4Categories = [] } = {},
    loading: loadingL4Categories,
    refetch: refetchL4Categories,
  } = useGet(GET_CATEGORIES_FOR_BRAND_CLEANUP, {
    category_level: L4_CATEGORY_LEVEL,
    global_vendor_id: selectedVendor?.value || null,
    brand_id: selectedBrand?.value || null,
    suggested_brand_id: selectedSuggestedBrand?.value || null,
    distributor_brand_name: selectedDistributorBrandName?.value || null,
    suggested_action: selectedSuggestedAction?.value || null,
    l2_category_id: selectedL2Category?.value || null,
    l3_category_id: selectedL3Category?.value || null,
    trainee_work_only: traineeWorkOnly || null,
    gmv_products_only: GMVProductsOnly || null,
    minimum_gmv: minimumGMV || null,
  });

  const { postData: generateReport } = usePost(
    FILE_EXPORTS_GENERATE_REPORTS,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () => {
      SwalDialog('error', 'An error occurred while Generating the File', 'Error', 'center');
    },
  );

  const handleExportReport = () => {
    generateReport({
      report_type: ReportType.DP_PRODUCTS_BRAND_REPORT,
      global_vendor_id: selectedVendor?.value,
    });
  };

  const { postData: cleanupBrandsOfSelectedProducts, loading: cleaningProducts } = usePost(
    CLEANUP_BRANDS_OF_SELECTED_PRODUCTS,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Brands of selected l4 category cleaned up successfully',
        title: 'Success',
        position: 'center',
      });
      setSelectedProductIds([]);
      setShowAssignBrandsSection(false);
      refetchProducts();
      refetchProductCount();
      refetchBrands();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  useEffect(() => {
    setSelectedProductIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    setSelectedL3Category(null);
    setSelectedL4Category(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedL2Category]);

  useEffect(() => {
    setSelectedL4Category(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedL3Category]);

  useEffect(() => {
    setSelectedProductIds([]);
    setShowAssignBrandsSection(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedL2Category, selectedL3Category, selectedL4Category]);

  useEffect(() => {
    if (!selectedBrand?.value || !selectedProductIds.length) {
      setShowAssignBrandsSection(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, selectedProductIds]);

  const handleRowClick = productId => {
    if (selectedProductIds.includes(productId)) {
      setSelectedProductIds(selectedProductIds.filter(id => id !== productId));
    } else {
      setSelectedProductIds([...selectedProductIds, productId]);
    }
  };

  const handleSelectAllOnPage = event => {
    if (event.target.checked) {
      // Select all products on current page
      const currentPageProductIds = products?.map(product => product.id) || [];
      const newSelectedIds = [...new Set([...selectedProductIds, ...currentPageProductIds])];
      setSelectedProductIds(newSelectedIds);
    } else {
      // Deselect all products on current page
      const currentPageProductIds = products?.map(product => product.id) || [];
      setSelectedProductIds(selectedProductIds.filter(id => !currentPageProductIds.includes(id)));
    }
  };

  // Check if all products on current page are selected
  const areAllOnPageSelected = () => {
    if (!products || products.length === 0) return false;
    return products.every(product => selectedProductIds.includes(product.id));
  };

  // Check if some (but not all) products on current page are selected
  const areSomeOnPageSelected = () => {
    if (!products || products.length === 0) return false;
    return (
      products.some(product => selectedProductIds.includes(product.id)) && !areAllOnPageSelected()
    );
  };

  return (
    <Container>
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="mb-0">Brand Cleanup</h1>
        </Col>
        <Col md="auto">
          {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleExportReport}
              disabled={!selectedVendor?.value}
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Export Products of vendor
            </Button>
          )}
        </Col>
      </Row>
      <BrandCleanupFilters
        brands={brands}
        refetchBrands={refetchBrands}
        loadingBrands={loadingBrands}
        suggestedBrands={suggestedBrands}
        loadingSuggestedBrands={loadingSuggestedBrands}
        refetchSuggestedBrands={refetchSuggestedBrands}
        distributorBrandNames={distributorBrandNames}
        loadingDistributorBrandNames={loadingDistributorBrandNames}
        refetchDistributorBrandNames={refetchDistributorBrandNames}
        l2Categories={l2Categories}
        loadingL2Categories={loadingL2Categories}
        refetchL2Categories={refetchL2Categories}
        l3Categories={l3Categories}
        loadingL3Categories={loadingL3Categories}
        refetchL3Categories={refetchL3Categories}
        l4Categories={l4Categories}
        loadingL4Categories={loadingL4Categories}
        refetchL4Categories={refetchL4Categories}
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedSuggestedBrand={selectedSuggestedBrand}
        setSelectedSuggestedBrand={setSelectedSuggestedBrand}
        selectedDistributorBrandName={selectedDistributorBrandName}
        setSelectedDistributorBrandName={setSelectedDistributorBrandName}
        selectedL2Category={selectedL2Category}
        setSelectedL2Category={setSelectedL2Category}
        selectedL3Category={selectedL3Category}
        setSelectedL3Category={setSelectedL3Category}
        selectedL4Category={selectedL4Category}
        setSelectedL4Category={setSelectedL4Category}
        selectedSuggestedAction={selectedSuggestedAction}
        setSelectedSuggestedAction={setSelectedSuggestedAction}
        selectedSimilarityScore={selectedSimilarityScore}
        setSelectedSimilarityScore={setSelectedSimilarityScore}
        traineeWorkOnly={traineeWorkOnly}
        setTraineeWorkOnly={setTraineeWorkOnly}
        hasCleanupBrandsPermission={hasCleanupBrandsPermission}
        GMVProductsOnly={GMVProductsOnly}
        setGMVProductsOnly={setGMVProductsOnly}
        minimumGMV={minimumGMV}
        setMinimumGMV={setMinimumGMV}
      />
      <Tabs defaultActiveKey={activeTabKey} onSelect={selectedKey => setActiveTabKey(selectedKey)}>
        <Tab
          eventKey={BRAND_CLEANUP_TABS.BRAND_CLEANUP}
          title={BRAND_CLEANUP_TABS_TITLE[BRAND_CLEANUP_TABS.BRAND_CLEANUP]}
        >
          <BrandCleanupActionsSection
            selectedProductIds={selectedProductIds}
            cleanupBrandsOfSelectedProducts={cleanupBrandsOfSelectedProducts}
            setShowBrandCleanupModal={setShowBrandCleanupModal}
            products={products}
            selectedBrand={selectedBrand}
            selectedSuggestedAction={selectedSuggestedAction}
            selectedSuggestedBrand={selectedSuggestedBrand}
            showAssignBrandsSection={showAssignBrandsSection}
            setShowAssignBrandsSection={setShowAssignBrandsSection}
            hasCleanupBrandsPermission={hasCleanupBrandsPermission}
          />
          {showAssignBrandsSection ? (
            <AssignBrandsSection
              selectedProductIds={selectedProductIds}
              cleanupBrandsOfSelectedProducts={cleanupBrandsOfSelectedProducts}
              cancel={() => setShowAssignBrandsSection(false)}
            />
          ) : null}
          <BrandCleanupProductsTable
            areAllOnPageSelected={areAllOnPageSelected}
            handleSelectAllOnPage={handleSelectAllOnPage}
            areSomeOnPageSelected={areSomeOnPageSelected}
            products={products}
            productCount={productCount}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            selectedProductIds={selectedProductIds}
            setSelectedProductIds={setSelectedProductIds}
            loadingProducts={loadingProducts}
            loadingProductCount={loadingProductCount}
            cleaningProducts={cleaningProducts}
            handleRowClick={handleRowClick}
            hasCleanupBrandsPermission={hasCleanupBrandsPermission}
            traineeWorkOnly={traineeWorkOnly}
            refetchProducts={refetchProducts}
            refetchProductCount={refetchProductCount}
            refetchBrands={refetchBrands}
            refetchSuggestedBrands={refetchSuggestedBrands}
            refetchDistributorBrandNames={refetchDistributorBrandNames}
            refetchL2Categories={refetchL2Categories}
            refetchL3Categories={refetchL3Categories}
            refetchL4Categories={refetchL4Categories}
            GMVProductsOnly={GMVProductsOnly}
          />
        </Tab>
        <Tab
          eventKey={BRAND_CLEANUP_TABS.DUPLICATE_VERIFIED_BRANDS}
          title={BRAND_CLEANUP_TABS_TITLE[BRAND_CLEANUP_TABS.DUPLICATE_VERIFIED_BRANDS]}
        >
          <DuplicateBrandsView selectedBrand={selectedBrand} selectedVendor={selectedVendor} />
        </Tab>
        <Tab
          eventKey={BRAND_CLEANUP_TABS.DUPLICATE_UNVERIFIED_BRANDS}
          title={BRAND_CLEANUP_TABS_TITLE[BRAND_CLEANUP_TABS.DUPLICATE_UNVERIFIED_BRANDS]}
        >
          <DuplicateUnverifiedBrandsView
            selectedBrand={selectedBrand}
            selectedVendor={selectedVendor}
          />
        </Tab>
      </Tabs>
      {showBrandCleanupModal ? (
        <BrandCleanupModal
          brandCleanupRecord={products.find(product => product.id === selectedProductIds[0])}
          onHide={() => {
            setShowBrandCleanupModal(false);
          }}
        />
      ) : null}
    </Container>
  );
}

export default BrandCleanupView;
